import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Link,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Upload, Eye, Download } from 'lucide-react';
import { useCaseStore } from '../store/caseStore';
import { useNavigate } from 'react-router-dom';
import { DocumentUploadDialog } from '../components/cases/Documents/DocumentUploadDialog';

export const Documents: React.FC = () => {
  const navigate = useNavigate();
  const { cases } = useCaseStore();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get all documents with case context
  const documentsWithContext = cases.flatMap(case_ => 
    case_.documents.map(doc => ({
      ...doc,
      caseId: case_.id,
      caseTitle: case_.title,
      caseNumber: case_.caseNumber
    }))
  ).sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());

  const getDocumentTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'legal':
        return 'primary';
      case 'medical':
        return 'success';
      case 'financial':
        return 'warning';
      case 'evidence':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleCaseClick = (caseId: string) => {
    // Navigate to the case's documents tab (tab index 3)
    navigate(`/cases/${caseId}`, { state: { activeTab: 3 } });
  };

  const DocumentCard = ({ doc }: { doc: typeof documentsWithContext[0] }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
              {doc.title}
            </Typography>
            <Chip
              label={doc.type}
              size="small"
              color={getDocumentTypeColor(doc.type)}
              sx={{ mb: 2 }}
            />
          </Box>

          <Box>
            <Link
              component="button"
              variant="body2"
              onClick={() => handleCaseClick(doc.caseId)}
              sx={{ 
                textAlign: 'left',
                display: 'block',
                lineHeight: 1.4,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {doc.caseTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.caseNumber}
              </Typography>
            </Link>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}>
            <Typography variant="caption" color="text.secondary">
              {new Date(doc.uploadDate).toLocaleDateString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Eye size={18} />
              </IconButton>
              <IconButton
                size="small"
                href={doc.url}
                download
              >
                <Download size={18} />
              </IconButton>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        gap: 2,
        flexWrap: 'wrap',
      }}>
        <Typography variant="h4" sx={{ mb: { xs: 0, sm: 0 } }}>Documents</Typography>
        <Button
          variant="contained"
          startIcon={<Upload />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{ 
            whiteSpace: 'nowrap',
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          Upload Document
        </Button>
      </Box>

      {isMobile ? (
        <Box>
          {documentsWithContext.map((doc) => (
            <DocumentCard key={doc.id} doc={doc} />
          ))}
          {documentsWithContext.length === 0 && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No documents have been uploaded yet
            </Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Case</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentsWithContext.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>{doc.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.type}
                      size="small"
                      color={getDocumentTypeColor(doc.type)}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleCaseClick(doc.caseId)}
                      sx={{ 
                        textAlign: 'left',
                        display: 'block',
                        lineHeight: 1.4,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {doc.caseTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.caseNumber}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {new Date(doc.uploadDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Eye size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        href={doc.url}
                        download
                      >
                        <Download size={18} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {documentsWithContext.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No documents have been uploaded yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={(document) => {
          // Handle document upload
          console.log('Document upload:', document);
          setUploadDialogOpen(false);
        }}
      />
    </Box>
  );
};